import { useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { supabase } from '@/integrations/supabase/client';
import { useMascotToast, useAuthRequiredToast } from '@/components/MascotToast';
import { useAuth } from '@/hooks/useAuth';

export interface Collection {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  cafeCount: number;
}

/**
 * Example list names shown as tappable chips when creating a list. These both
 * lower the blank-slate friction of naming a list AND seed the analytics: when
 * a user taps one we tag `from_suggestion`, so we can see which prompts resonate
 * vs. what people type on their own (i.e. what they're actually looking for).
 */
export const LIST_NAME_SUGGESTIONS = [
  "Quiet for calls",
  "Good for deep work",
  "Near the office",
  "Weekend spots",
  "Date spots",
  "Great coffee",
] as const;

/** Where a list was created — used for analytics segmentation. */
export type CollectionCreateSource = "add_to_list_dialog" | "favorites_page";

interface CreateCollectionMeta {
  source?: CollectionCreateSource;
  fromSuggestion?: boolean;
}

// Lists ("WiFi spots", "Date ideas") layered on top of the flat user_favorites
// set. Invariant: anything added to a collection is also in user_favorites, so
// the saved state and list membership never contradict each other.
export function useCollections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { mascotToast } = useMascotToast();
  const { showAuthRequired } = useAuthRequiredToast();

  const fetchCollections = useCallback(async () => {
    if (!userId) {
      setCollections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const { data, error } = await supabase
      .from('collections')
      .select('id, name, created_at, updated_at, collection_cafes(count)')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching collections:', error);
      setCollections([]);
    } else {
      setCollections(
        (data || []).map((c) => ({
          id: c.id,
          name: c.name,
          created_at: c.created_at,
          updated_at: c.updated_at,
          // PostgREST returns the aggregate as [{ count: n }]
          cafeCount: (c.collection_cafes as { count: number }[] | null)?.[0]?.count ?? 0,
        }))
      );
    }
    setIsLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const createCollection = useCallback(
    async (name: string, meta?: CreateCollectionMeta): Promise<Collection | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      if (!userId) {
        showAuthRequired('create collections');
        return null;
      }

      const { data, error } = await supabase
        .from('collections')
        .insert({ user_id: userId, name: trimmed })
        .select('id, name, created_at, updated_at')
        .single();

      if (error) {
        // Unique violation on (user_id, name)
        const duplicate = error.code === '23505';
        if (!duplicate) console.error('Error creating collection:', error);
        mascotToast({
          title: duplicate ? 'Name already used' : 'Error',
          description: duplicate
            ? 'You already have a list with that name.'
            : 'Failed to create collection.',
          mood: 'sad',
          variant: 'destructive',
        });
        return null;
      }

      const created: Collection = { ...data, cafeCount: 0 };
      setCollections((prev) => [...prev, created]);
      // Capture the name itself so we can learn what people organize by /
      // what they're looking for. `from_suggestion` distinguishes our seeded
      // prompts from names users came up with on their own.
      try {
        posthog.capture('collection_created', {
          collection_id: created.id,
          name: created.name,
          name_normalized: created.name.toLowerCase(),
          name_length: created.name.length,
          word_count: created.name.split(/\s+/).filter(Boolean).length,
          source: meta?.source ?? 'unknown',
          from_suggestion: meta?.fromSuggestion ?? false,
        });
      } catch { /* ignore */ }
      return created;
    },
    [userId, mascotToast, showAuthRequired]
  );

  const renameCollection = useCallback(
    async (collectionId: string, name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed || !userId) return false;

      const { error } = await supabase
        .from('collections')
        .update({ name: trimmed, updated_at: new Date().toISOString() })
        .eq('id', collectionId)
        .eq('user_id', userId);

      if (error) {
        const duplicate = error.code === '23505';
        mascotToast({
          title: duplicate ? 'Name already used' : 'Error',
          description: duplicate
            ? 'You already have a list with that name.'
            : 'Failed to rename collection.',
          mood: 'sad',
          variant: 'destructive',
        });
        return false;
      }

      setCollections((prev) =>
        prev.map((c) => (c.id === collectionId ? { ...c, name: trimmed } : c))
      );
      return true;
    },
    [userId, mascotToast]
  );

  const deleteCollection = useCallback(
    async (collectionId: string): Promise<boolean> => {
      if (!userId) return false;

      const { error } = await supabase
        .from('collections')
        .delete()
        .eq('id', collectionId)
        .eq('user_id', userId);

      if (error) {
        mascotToast({
          title: 'Error',
          description: 'Failed to delete collection.',
          mood: 'sad',
          variant: 'destructive',
        });
        return false;
      }

      setCollections((prev) => prev.filter((c) => c.id !== collectionId));
      try {
        posthog.capture('collection_deleted', { collection_id: collectionId });
      } catch { /* ignore */ }
      return true;
    },
    [userId, mascotToast]
  );

  // Adds a cafe to a collection AND guarantees it is in user_favorites (the
  // invariant). Favoriting first is idempotent thanks to the UNIQUE(user_id,
  // cafe_id) constraint — we ignore a duplicate-key error there.
  const addCafeToCollection = useCallback(
    async (collectionId: string, cafeId: string): Promise<boolean> => {
      if (!userId) {
        showAuthRequired('save to collections');
        return false;
      }

      const { error: favError } = await supabase
        .from('user_favorites')
        .insert({ user_id: userId, cafe_id: cafeId });
      // 23505 = already favorited, which is fine.
      if (favError && favError.code !== '23505') {
        console.error('Error favoriting cafe:', favError);
      }

      const { error } = await supabase
        .from('collection_cafes')
        .insert({ collection_id: collectionId, cafe_id: cafeId });

      // 23505 = already in this collection, treat as success.
      if (error && error.code !== '23505') {
        mascotToast({
          title: 'Error',
          description: 'Failed to add to collection.',
          mood: 'sad',
          variant: 'destructive',
        });
        return false;
      }

      if (!error) {
        setCollections((prev) =>
          prev.map((c) =>
            c.id === collectionId ? { ...c, cafeCount: c.cafeCount + 1 } : c
          )
        );
        try {
          posthog.capture('cafe_added_to_collection', {
            collection_id: collectionId,
            cafe_id: cafeId,
          });
        } catch { /* ignore */ }
      }
      return true;
    },
    [userId, mascotToast, showAuthRequired]
  );

  const removeCafeFromCollection = useCallback(
    async (collectionId: string, cafeId: string): Promise<boolean> => {
      if (!userId) return false;

      const { error } = await supabase
        .from('collection_cafes')
        .delete()
        .eq('collection_id', collectionId)
        .eq('cafe_id', cafeId);

      if (error) {
        mascotToast({
          title: 'Error',
          description: 'Failed to remove from collection.',
          mood: 'sad',
          variant: 'destructive',
        });
        return false;
      }

      setCollections((prev) =>
        prev.map((c) =>
          c.id === collectionId
            ? { ...c, cafeCount: Math.max(0, c.cafeCount - 1) }
            : c
        )
      );
      return true;
    },
    [userId, mascotToast]
  );

  return {
    collections,
    isLoading,
    isAuthenticated: !!userId,
    refresh: fetchCollections,
    createCollection,
    renameCollection,
    deleteCollection,
    addCafeToCollection,
    removeCafeFromCollection,
  };
}

// Which of the user's collections contain a given cafe. Used by the
// "Add to collection" picker to show current membership.
export function useCafeCollectionMembership(cafeId?: string) {
  const [collectionIds, setCollectionIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const fetchMembership = useCallback(async () => {
    if (!cafeId || !userId) {
      setCollectionIds([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // RLS already scopes collection_cafes to the owner via the parent
    // collection, so filtering by cafe_id is sufficient.
    const { data, error } = await supabase
      .from('collection_cafes')
      .select('collection_id')
      .eq('cafe_id', cafeId);

    if (error) {
      console.error('Error fetching collection membership:', error);
      setCollectionIds([]);
    } else {
      setCollectionIds((data || []).map((r) => r.collection_id));
    }
    setIsLoading(false);
  }, [cafeId, userId]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  return { collectionIds, isLoading, refresh: fetchMembership };
}
